import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import HomeTemplate from '../templates/HomeTemplate'
import { Calendar, Clock, User, Tag, Search, Filter } from 'lucide-react'
import { BLOG_POSTS } from '../data/blogPosts.jsx'
import useCanonicalUrl from '../hooks/useCanonicalUrl'
import './BlogListingPage.css'

const BlogListingPage = () => {
  useCanonicalUrl();
  const [filteredPosts, setFilteredPosts] = useState(BLOG_POSTS)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [selectedTag, setSelectedTag] = useState('All')

  // Get unique categories and tags
  const categories = ['All', ...new Set(BLOG_POSTS.map(post => post.category))]
  const tags = ['All', ...new Set(BLOG_POSTS.flatMap(post => post.tags))]

  useEffect(() => {
    document.title = 'Blog - EveryKits | Tips, Guides & Tutorials'

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Discover expert tips, guides, and tutorials on image optimization, password security, QR codes, SEO writing, and more. Learn how to use EveryKits tools effectively.')
    }

    // Add structured data for blog listing
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "EveryKits Blog",
      "description": "Expert tips, guides, and tutorials for online tools and digital productivity",
      "url": "https://everykits.com/blog",
      "blogPost": BLOG_POSTS.map(post => ({
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.description,
        "url": `https://everykits.com/blog/${post.slug}/`,
        "datePublished": post.publishDate,
        "author": {
          "@type": "Organization",
          "name": post.author
        },
        "publisher": {
          "@type": "Organization",
          "name": "EveryKits",
          "logo": {
            "@type": "ImageObject",
            "url": "https://everykits.com/logo.png"
          }
        },
        "image": `https://everykits.com${post.featuredImage}`,
        "keywords": post.tags.join(', ')
      }))
    }

    // Add structured data to head
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(structuredData)
    document.head.appendChild(script)

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  useEffect(() => {
    let filtered = BLOG_POSTS

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    }

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(post => post.category === selectedCategory)
    }

    // Filter by tag
    if (selectedTag !== 'All') {
      filtered = filtered.filter(post => post.tags.includes(selectedTag))
    }

    setFilteredPosts(filtered)
  }, [searchTerm, selectedCategory, selectedTag])

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }
    return new Date(dateString).toLocaleDateString('en-US', options)
  }

  return (
    <HomeTemplate>
      <div className="blog-listing">
        {/* Hero Section */}
        <section className="blog-hero">
          <div className="hero-content">
            <span className="hero-eyebrow">Guides &amp; Tutorials</span>
            <h1>EveryKits Blog</h1>
            <p>Practical guides on image compression, password security, QR codes, and SEO writing — plus the free tools to put every one of them to work.</p>
          </div>
        </section>

        {/* Search and Filter Section */}
        <section className="blog-filters">
          <div className="search-container">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-controls">
            <div className="filter-group">
              <Filter className="filter-icon" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="filter-select"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <Tag className="filter-icon" />
              <select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                className="filter-select"
              >
                {tags.map(tag => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="results-count">
            {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'} found
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="blog-posts-grid">
          {filteredPosts.length > 0 ? (
            filteredPosts.map((post, index) => (
              <article
                key={post.id}
                className="blog-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="blog-card-image">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    onError={(e) => {
                      e.target.src = '/logo/default-blog-image.jpg'
                    }}
                  />
                  <div className="blog-card-category">
                    {post.category}
                  </div>
                </div>

                <div className="blog-card-content">
                  <h2 className="blog-card-title">
                    <Link to={`/blog/${post.slug}/`}>
                      {post.title}
                    </Link>
                  </h2>

                  <p className="blog-card-description">
                    {post.description}
                  </p>

                  <div className="blog-card-meta">
                    <div className="meta-item">
                      <User className="meta-icon" />
                      <span>{post.author}</span>
                    </div>
                    <div className="meta-item">
                      <Calendar className="meta-icon" />
                      <span suppressHydrationWarning>{formatDate(post.publishDate)}</span>
                    </div>
                    <div className="meta-item">
                      <Clock className="meta-icon" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>

                  <div className="blog-card-tags">
                    {post.tags.slice(0, 3).map((tag, tagIndex) => (
                      <span key={tagIndex} className="tag">
                        {tag}
                      </span>
                    ))}
                    {post.tags.length > 3 && (
                      <span className="tag-more">
                        +{post.tags.length - 3} more
                      </span>
                    )}
                  </div>

                  <div className="blog-card-footer">
                    <Link to={`/blog/${post.slug}/`} className="read-more-btn">
                      Read More
                    </Link>
                  </div>
                </div>
              </article>
            ))
          ) : (
            <div className="no-results">
              <h3>No articles found</h3>
              <p>Try adjusting your search terms or filters to find what you're looking for.</p>
            </div>
          )}
        </section>




      </div>
    </HomeTemplate>
  )
}

export default BlogListingPage